import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PageControl } from '../model/page-control';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { Group, GroupPageableResponse, SimplifiedGroupHelper } from '@app-model/group';
import { PromotedContent } from '@app-model/promotedContent';
import { FileUploader } from '@app-model/client';
import { map } from 'rxjs/operators';
import { DefaultStatus } from '@app-model/login';
@Injectable({
	providedIn: 'root'
})
export class GroupService {
	constructor(private http: HttpClient, private toastr: ToastrService) {}

	get(id: string): Observable<Group> {
		return this.http.get<Group>(`${environment.api}/group/${id}`).pipe(
			map((response: Group) => {
				return response;
			})
		);
	}

	paginate(pageControl: PageControl, clientId?: string, searchTerm?: string): Observable<GroupPageableResponse> {
		const body: any = {
			page: pageControl.page,
			pageSize: pageControl.limit,
			sortDirection: pageControl.order,
			sortField: pageControl.orderField
		};

		if (pageControl.param && pageControl.param !== '' && pageControl.value && pageControl.value !== '') {
			body.param = pageControl.param;
			body.value = pageControl.value;
		}
		const params = {};
		if (clientId) {
			params['clientId'] = clientId;
		}
		if (searchTerm) {
			params['query'] = searchTerm;
		}

		return this.http.post<GroupPageableResponse>(`${environment.api}/group/list`, body, { params });
	}

	getGroupOptions(clientId?: string): Observable<SimplifiedGroupHelper[]> {
		const params = {};
		if (clientId) {
			params['clientId'] = clientId;
		}
		return this.http.get<SimplifiedGroupHelper[]>(`${environment.api}/group/options`, { params });
	}

	clientList(pageControl: PageControl, clientId?: string): Observable<GroupPageableResponse> {
		const body: any = {
			page: pageControl.page,
			pageSize: pageControl.limit,
			sortDirection: pageControl.order,
			sortField: pageControl.orderField
		};

		if (pageControl.param && pageControl.param !== '' && pageControl.value && pageControl.value !== '') {
			body.param = pageControl.param;
			body.value = pageControl.value;
		}
		const params = {};
		if (clientId) {
			params['clientId'] = clientId;
		}

		return this.http.post<GroupPageableResponse>(`${environment.api}/client/list`, body, { params });
	}

	getSimplifiedGroup(id: string): Observable<SimplifiedGroupHelper> {
		return this.http.get<SimplifiedGroupHelper>(`${environment.api}/group/simplified/${id}`);
	}

	getPromotedContents(groupId: string): Observable<PromotedContent[]> {
		return this.http.get<PromotedContent[]>(`${environment.api}/group/promotedContents/${groupId}`);
	}

	savePromotedContents(groupId: string, promotedContents: PromotedContent[]): Observable<PromotedContent[]> {
		return this.http.post<PromotedContent[]>(
			`${environment.api}/group/savePromotedContents/${groupId}`,
			promotedContents
		);
	}

	uploadBannerImage(file: File, groupId: string): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/group/uploadPromotionBanner/${groupId}`, body);
	}

	uploadImageGridImage(file: File, groupId: string): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/group/uploadPromotionGrid/${groupId}`, body);
	}

	uploadCarrouselImage(file: File, groupId: string, fileType: string): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(
			`${environment.api}/group/uploadPromotionCarrousel/${groupId}/${fileType}`,
			body
		);
	}

	uploadPdf(file: File, groupId: string): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/group/uploadPdf/${groupId}`, body);
	}

	uploadGroupHero(file: File): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/group/uploadHero`, body);
	}

	uploadGroupFavicon(file: File): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/group/uploadFavicon`, body);
	}

	uploadGroupLogo(file: File): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/group/uploadLogo`, body);
	}

	uploadGroupSecondaryLogo(file: File): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/group/uploadLogo`, body);
	}

	uploadGroupHeroUpdate(file: File, groupId: string): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/group/uploadHero/${groupId}`, body);
	}

	uploadGroupFaviconUpdate(file: File, groupId: string): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/group/uploadFavicon/${groupId}`, body);
	}

	uploadGroupLogoUpdate(file: File, groupId: string): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/group/uploadLogo/${groupId}`, body);
	}

	uploadGroupSecondaryLogoUpdate(file: File, groupId: string): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/group/uploadLogo/${groupId}`, body);
	}

	saveGroup(body: Group): Observable<any> {
		delete body.cardholderInfo;
		delete body.type;
		return this.http.post(`${environment.api}/group`, body);
	}

	remove(id: string): Observable<DefaultStatus> {
		return this.http.post<DefaultStatus>(`${environment.api}/group/remove/${id}`, {});
	}

	syncConfig(groupId, configId: string): Observable<DefaultStatus> {
		return this.http.post<DefaultStatus>(`${environment.api}/group/runIntegrationSync/${groupId}/${configId}`, {});
	}
}
